import { createContext, useContext, useEffect } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * How the loaders scattered across the app tell the first-load overlay that
 * they are still working, without having to know it exists. See
 * `FirstLoadOverlay` for what the count drives.
 */
type FirstLoadGateState = {
  /** Loaders currently mounted and holding the first-load overlay open. */
  blockerCount: number;
  /** One-way latch: the overlay is a first-load-only affordance. */
  isRevealed: boolean;
  /**
   * Loaders currently on screen, for the whole session — unlike `blockerCount`
   * it never latches. The Data Inspector reads it to time every page visit
   * from navigation until the last loader clears. Nothing in React selects it.
   */
  activeLoaderCount: number;
};

type FirstLoadGateActions = {
  addBlocker: () => void;
  removeBlocker: () => void;
  reveal: () => void;
};

export type FirstLoadGateStore = FirstLoadGateState & FirstLoadGateActions;

export const useFirstLoadGateStore = create<FirstLoadGateStore>()(
  devtools(
    (set) => ({
      blockerCount: 0,
      isRevealed: false,
      activeLoaderCount: 0,
      // The overlay's own count stops moving once revealed — the overlay is
      // gone and would otherwise pay for bookkeeping nothing reads. The live
      // count keeps going for the inspector.
      addBlocker: () =>
        set((state) => ({
          activeLoaderCount: state.activeLoaderCount + 1,
          ...(state.isRevealed ? {} : { blockerCount: state.blockerCount + 1 }),
        })),
      removeBlocker: () =>
        set((state) => ({
          activeLoaderCount: Math.max(0, state.activeLoaderCount - 1),
          ...(state.isRevealed
            ? {}
            : { blockerCount: Math.max(0, state.blockerCount - 1) }),
        })),
      reveal: () => set({ isRevealed: true, blockerCount: 0 }),
    }),
    { name: 'first-load-gate' }
  )
);

/**
 * Holds the first-load overlay open while `active`, and counts towards the
 * session-long `activeLoaderCount` for as long as it stays mounted.
 *
 * The shared loaders register themselves, which is what makes the gate cover
 * every route: whatever a page shows while it works — a page-level loader, a
 * chart loader — keeps the overlay up. Routes only need to call this directly
 * when they render nothing at all while loading.
 *
 * A subtree wrapped in `FirstLoadGateSuppressionContext` (value `true`) opts
 * out entirely — its loaders never hold the overlay. This is for surfaces that
 * are NOT the first visible page: the global assistant's offscreen/secondary
 * hosts (headless digest warm-up, drawer chart previews) mount whole
 * FilterProviders whose loaders would otherwise keep the cover up until those
 * background requests finish.
 */
export const FirstLoadGateSuppressionContext = createContext(false);

export const useFirstLoadGateBlocker = (active: boolean) => {
  const suppressed = useContext(FirstLoadGateSuppressionContext);
  useEffect(() => {
    if (suppressed || !active) return;

    const { addBlocker, removeBlocker } = useFirstLoadGateStore.getState();
    addBlocker();
    return removeBlocker;
  }, [active, suppressed]);
};
